'use client';

import { useId, useState, type ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';
import { Icon } from '@/shared/ui/icon';

import styles from './accordion.module.scss';

export type AccordionItemProps = {
  title: ReactNode;
  children: ReactNode;
  /** Uncontrolled initial state. */
  defaultOpen?: boolean;
  /** Controlled state; pass together with onToggle. */
  open?: boolean;
  onToggle?: (open: boolean) => void;
  /** "plus" — "+/−" marker; "rotate" — plus icon rotated 45° when open. */
  marker?: 'plus' | 'rotate';
  className?: string | undefined;
  headerClassName?: string | undefined;
  panelClassName?: string | undefined;
};

export function AccordionItem({
  title,
  children,
  defaultOpen = false,
  open,
  onToggle,
  marker = 'plus',
  className,
  headerClassName,
  panelClassName,
}: AccordionItemProps) {
  const [innerOpen, setInnerOpen] = useState(defaultOpen);
  const isOpen = open ?? innerOpen;
  const id = useId();
  const toggle = () => {
    const next = !isOpen;
    if (open === undefined) setInnerOpen(next);
    onToggle?.(next);
  };
  return (
    <div className={cx(styles.root, className)} data-open={isOpen || undefined}>
      <h3 className={styles.heading}>
        <button
          type="button"
          className={cx(styles.trigger, headerClassName)}
          aria-expanded={isOpen}
          aria-controls={`${id}-panel`}
          id={`${id}-trigger`}
          onClick={toggle}
        >
          <span className={styles.title}>{title}</span>
          <span className={styles.marker} data-marker={marker} aria-hidden="true">
            {marker === 'rotate' ? (
              <Icon name="plus" size={22} strokeWidth={1.3} />
            ) : isOpen ? (
              '−'
            ) : (
              '+'
            )}
          </span>
        </button>
      </h3>
      <section
        id={`${id}-panel`}
        aria-labelledby={`${id}-trigger`}
        hidden={!isOpen}
        className={cx(styles.panel, panelClassName)}
      >
        {children}
      </section>
    </div>
  );
}
